import React, { Suspense, lazy } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Switch, Route, Link } from 'react-router-dom';
import { Menu } from 'semantic-ui-react';
import _ from 'lodash';
import styled from 'styled-components';
import { Pane } from 'react-split-pane';
import StyledLink from 'common/components/atoms/Link';
import ErrorBoundary from 'common/components/ErrorBoundary/ErrorBoundary';
import { withTranslation } from 'react-i18next';
import { applyBrowserDuplicateBlocker, history } from 'common/helpers/history';
import lazyRetry from 'common/helpers/componentLoader';
import LoadingSvg from 'common/components/atoms/Loading';
import FlexContainer from 'common/components/atoms/FlexContainer';
import { ChevronRight, ChevronLeft, User, Home, QuestionCircle } from 'common/images/FaIcons';
import PageNotFound from 'common/components/PageNotFound/PageNotFound';
import Overlay from 'common/components/atoms/Overlay';
import AlertPrompt from 'common/components/organisms/AlertPrompt';
import { isPartnerAdmin, isPetasenseAdmin, isPetasenseViewer, isOrgAdmin, isAnalyst, isAdmin, isFmeaEnabled } from 'common/rbac/util';
import DynamicTabs from 'common/components/organisms/DynamicTabs';

import MachineDetails from 'home/Machines/MachineDetails/MachineDetails';
import HomePageModals from 'home/HomePage/components/HomePageModals';
import TaskDetailsModal from 'home/HomePage/components/TaskDetailsModal';
import RepairDetailsModal from 'home/HomePage/components/RepairDetailsModal';
import * as taskActions from 'home/HomePage/actions/tasks.actions';
import * as repairActions from 'home/HomePage/actions/repairs.actions';
import { getPreferredTimezone, setPreferredTimezone } from 'common/helpers/time';
import * as assetDetailsActions from '../AssetHierarchy/actions/assetDetails.actions';
import * as hierarchyActions from '../AssetHierarchy/actions/hierarchy.actions';
import ProfileDropdown from '../Menu/ProfileDropdown';
import HierarchyView from '../AssetHierarchy/components/pages/HierarchyView';
import OrgSiteBreadcrumb from '../Menu/OrgSiteBreadcrumb';
import BreadCrumbHeader from '../Menu/BreadCrumbHeader';
import { isOrgDashboardRoute, getOrgIdFromPath } from '../Organisation/orgRouteUtils';
import OrgSiteHierarchyPanel from '../Organisation/OrgSiteHierarchy';
import { ORG_SITE_NAV_ENABLED } from '../Organisation/constants/orgDashboard.constants';
import { selectAccount } from '../../onboarding/user.actions';
import * as breadcrumbActions from '../Menu/actions/breadcrumb.actions';
import * as adminDashboardActions from '../AdminDashboard/adminDashboard.actions';
import * as hierarchyUtils from '../AssetHierarchy/utils/assetHierarchyUtils';
import HomePageContainer from './components/atoms/HomePageContainer';
import ChevronContainer from './components/atoms/ChevronContainer';
import SubscriptionBanner from './components/SubscriptionBanner';
import AnalystSummaryView from './AnalystSummaryView';
import { isUnsavedAnalystSummaryExists, getFlattenedAccountMenuRoutes } from './utils';
import {
  getHomeTabItems,
  tabsOnlyForPetasenseAdmins,
  tabsForPetasenseAndPartnerAdmins,
  tabsOnlyForAnalystsAndAdmins,
  tabsOnlyForAnalysts,
  tabsOnlyForOrgAdminsAndPetasenseAdmins,
  pathsWithoutAssetHierarchy,
  Routes
} from './constants/routes.constants';
import AdminDashboard from '../AdminDashboard/components/AdminDashboard';
import ReleaseNotesBell from '../ReleaseNotes/components/ReleaseNotesBell/ReleaseNotesBell';
import { CustomSplitPane } from './components/atoms/CustomSplitPane';

const SensorAdd = lazy(() => lazyRetry(() => import('home/Sensors/SensorAdd/components/SensorAdd')));
const TemplateDetails = lazy(() => lazyRetry(() => import('home/Templates/components/pages/TemplateDetails')));
const ProfileSettings = lazy(() => lazyRetry(() => import('home/ProfileSettings/ProfileSettings')));
const MachineBuilderModal = lazy(() => lazyRetry(() => import('../Machines/MachineBuilder/MachineBuilderModal')));
const MachineAdd = lazy(() => lazyRetry(() => import('../Machines/MachineAdd/components/MachineAdd')));
const ChartsView = lazy(() => lazyRetry(() => import('../AssetHierarchy/components/pages/ChartsView')));
const LocationChartView = lazy(() => lazyRetry(() => import('../AssetHierarchy/components/pages/LocationChartView')));
const MultilineChartView = lazy(() => lazyRetry(() => import('../AssetHierarchy/components/pages/MultilineChartView')));
const DataOverview = lazy(() => lazyRetry(() => import('../AssetHierarchy/components/pages/DataOverview')));
const OrgViewComponent = lazy(() => lazyRetry(() => import('../Organisation/OrgView')));

const Content = styled.div`
  width: 100%;
  flex-shrink: 0;
`;

const SwitchContainer = styled.div`
  height: 100%;
  ${props => props.background && `background: ${props.background};`}
`;

const MainContainer = styled(FlexContainer)`
  position: relative;
`;

const HierarchyContainer = styled(FlexContainer)`
  width: 100%;
  height: calc(100vh - var(--title-bar-offset, 0px) - 55px);
`;

// visualViewport is undefined in some older/embedded webviews; fall back to
// innerWidth so pane sizing never throws on those. (No optional chaining per repo rule.)
const viewportWidth = () =>
  ((window.visualViewport && window.visualViewport.width) || window.innerWidth);

class HomePage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeItem: 'overview',
      editMode: false,
      analystSummaryWarning: false,
      titleBarHeight: null,
      // if user switches account
      discardAnalystSummary: false,
      // Live pane width while the split-pane resizer is dragged. Pane2's CSS
      // width is derived from this value; without it, Pane1 grows over the
      // dashboard until onDragFinished commits to Redux.
      draggingPaneWidth: null,
    };

    this.tabItems = getHomeTabItems(props.t).map(item => item.key);
    this.analystSummaryRef = React.createRef();
  }

  componentDidMount() {
    applyBrowserDuplicateBlocker();
    this.updateTimezoneIfRequired();
    const { getHierarchy } = this.props.assetDetailsActions;
    const { account_id } = this.props.user.user;
    // directly machine route is called
    let machine_id = null;
    let config_id = null;
    let location_id = null;
    const path = this.props.history.location.pathname.split('/');
    if (path[1] === 'machines' && path.length > 2) machine_id = Number(path[2]);
    if (path[1] === 'configs' && path.length > 2) config_id = Number(path[2]);
    if (path[1] === 'location' && path.length > 2) location_id = Number(path[2]);
    if (this.props.adminDashboard.adminOpen) this.props.adminDashboardActions.toggleAdminDashboard();

    // Check if account switch is needed first
    const urlParams = new URLSearchParams(this.props.location.search);
    const urlAccountId = urlParams.get('account_id');
    const needsAccountSwitch = urlAccountId && parseInt(urlAccountId, 10) !== account_id;

    if (needsAccountSwitch) {
      // Let switchAccountifRequired handle everything (it will call getHierarchy and redirect)
      this.switchAccountifRequired();
    } else {
      // No account switch needed, proceed normally
      getHierarchy(account_id).then(() => {
        const machineTab = this.getMachineTab(path);
        if (machine_id) this.handleNodeClick(machine_id, 'machine', machineTab, false, this.props.history.location);
        if (config_id || path[1] === 'configs') this.handleNodeClick(config_id, 'config');
        if (location_id) this.handleNodeClick(location_id, 'location');
      }).catch(() => {});
    }
    this.componentDidUpdate();
  }

  componentDidUpdate(prevProps) {
    this.updateTimezoneIfRequired(prevProps);
    const item = this.props.location.pathname.substring(1);
    const { activeItem } = this.state;
    // make this logic better
    if (item === '' && activeItem !== 'overview') this.setState({ activeItem: 'overview' });
    if (item === '' || this.tabItems.indexOf(item) !== -1) {
      if (activeItem !== item && item !== '') this.setState({ activeItem: item || 'overview' });
    } else if (activeItem !== null) this.setState({ activeItem: null });
    if (item === '' && activeItem === null) this.setState({ activeItem: 'overview' });

    const enteredOrgView = (
      prevProps &&
      !isOrgDashboardRoute(prevProps.location.pathname) &&
      isOrgDashboardRoute(this.props.location.pathname)
    );
    if (
      enteredOrgView &&
      !ORG_SITE_NAV_ENABLED &&
      this.props.hierarchyViewPane &&
      this.props.hierarchyViewPane.currentWidth
    ) {
      this.props.hierarchyActions.toggleHierarchyView();
    }

    // Drop the live drag width once Redux has the committed size, so collapse
    // / expand via the chevron can drive effectiveWidth again.
    if (
      prevProps &&
      this.state.draggingPaneWidth != null &&
      this.props.hierarchyViewPane &&
      this.props.hierarchyViewPane.currentWidth === this.state.draggingPaneWidth &&
      prevProps.hierarchyViewPane &&
      prevProps.hierarchyViewPane.currentWidth !== this.props.hierarchyViewPane.currentWidth
    ) {
      this.setState({ draggingPaneWidth: null });
    }

    let machine_id = null;
    let config_id = null;
    let location_id = null;
    if (prevProps && (
      !_.isEqual(_.omit(prevProps.location, ['key']), _.omit(this.props.location, ['key'])) ||
      (!prevProps.breadcrumb.machine.id && prevProps.breadcrumb.machine.id !== this.props.breadcrumb.machine.id)
    )) {
      const path = this.props.history.location.pathname.split('/');
      if (path[1] === 'machines' && path.length > 2) machine_id = Number(path[2]);
      if (path[1] === 'configs' && path.length > 2) config_id = Number(path[2]);
      if (path[1] === 'location' && path.length > 2) location_id = Number(path[2]);
      if (machine_id) {
        const machineTab = this.getMachineTab(path);
        this.handleNodeClick(machine_id, 'machine', machineTab, false, this.props.history.location);
      } else if (config_id || path[1] === 'configs') {
        this.handleNodeClick(config_id, 'config');
        if (!this.props.hierarchyViewPane.currentWidth) {
          this.props.hierarchyActions.toggleHierarchyView();
        }
      } else if (location_id) {
        this.handleNodeClick(location_id, 'location');
        if (!this.props.hierarchyViewPane.currentWidth) {
          this.props.hierarchyActions.toggleHierarchyView();
        }
      }
    }
  }

  getMachineTab = path => path.length > 3 ? path[3] : 'overview'

  updateTimezoneIfRequired = (prevProps) => {
    const { user } = this.props;
    if (
      !prevProps ||
      (user.user && !_.isEqual(user.user, prevProps.user.user))
    ) {
      const timezone = getPreferredTimezone(user.user, this.props.currentAccount);
      setPreferredTimezone(timezone);
    }
  }

  updateConfig = (config) => {
    const path = this.getpath(config.config_id, 'config');
    const params = hierarchyUtils.getHierarchyParams(path, config.config_id, 'config');
    this.props.breadcrumbActions.updateBreadcrumb(params);
  };

  selectConfig = (node_id, nodePropertyType, nodePropertyValue, clearQueryParams = false) => {
    const { hierarchy } = this.props.assetHierarchy;

    // Check if hierarchy is still loading
    if (hierarchy && hierarchy.loading) {
      // Wait for hierarchy to load, then retry
      const checkInterval = setInterval(() => {
        const { hierarchy: currentHierarchy } = this.props.assetHierarchy;
        if (!currentHierarchy || !currentHierarchy.loading) {
          clearInterval(checkInterval);
          this.selectConfig(node_id, nodePropertyType, nodePropertyValue, clearQueryParams);
        }
      }, 100);
      // Set a timeout to prevent infinite waiting
      setTimeout(() => clearInterval(checkInterval), 10000);
      return;
    }

    const config_id = this.props.hierarchyActions.selectNode(node_id, 'config', nodePropertyType, nodePropertyValue);
    if (!config_id) return;
    const { sites } = this.props.assetHierarchy;
    const { machine_id, site_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(this.props.breadcrumb);
    const { getConfigs, getTags } = this.props.assetDetailsActions;
    const configList = hierarchyUtils.getConfigList(machine_id, site_id, sites);
    let search = this.props.location.search;
    if (clearQueryParams) search = '';
    if (configList && configList.length > 0) {
      let config = {};
      config = configList.find(item => item.config_id === config_id);
      if (!config) {
        getTags(machine_id, site_id);
        this.loading = true;
        getConfigs(machine_id, site_id).then(
          (res) => {
            if (!res) {
              this.props.history.push('/');
              return;
            }
            config = res.find(item => item.config_id === config_id);
            this.loading = false;
            this.setState({ config });
            this.props.history.replace({
              pathname: `/configs/${config_id}`,
              search
            });
          }
        );
      } else {
        this.setState({ config });
        this.props.history.replace({
          pathname: `/configs/${config_id}`,
          search
        });
      }
    } else {
      if (!site_id || !machine_id) return;
      getTags(machine_id, site_id);
      this.loading = true;
      getConfigs(machine_id, site_id).then(
        (res) => {
          if (!res) {
            this.props.history.push('/');
            return;
          }
          const config = res.find(item => item.config_id === config_id);
          this.loading = false;
          this.setState({ config });
          this.props.history.replace({
            pathname: `/configs/${config_id}`,
            search
          });
        }
      );
    }
  }

  selectLocation = (location_id) => {
    const { sites, hierarchy } = this.props.assetHierarchy;
    const isBearing = hierarchyUtils.isBearing(hierarchy, location_id);
    if (!isBearing) return;
    this.props.hierarchyActions.selectNode(location_id, 'location');
    const { machine_id, site_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(this.props.breadcrumb);
    const { getConfigs, getTags } = this.props.assetDetailsActions;
    const configList = hierarchyUtils.getConfigList(machine_id, site_id, sites);
    const configIds = hierarchyUtils.getConfigsFromLocationId(hierarchy, location_id).map(c => c.id);
    if (configList.length > 0) {
      let configs = [];
      configs = configList.filter(i => _.includes(configIds, i.config_id));
      if (configs.length !== configIds.length) {
        getTags(machine_id, site_id);
        getConfigs(machine_id, site_id).then(
          (res) => {
            if (!res) {
              this.props.history.push('/');
            }
            this.props.history.push(`/location/${location_id}`);
          }
        );
      } else {
        this.props.history.push(`/location/${location_id}`);
      }
    } else {
      if (!site_id || !machine_id) return;
      getTags(machine_id, site_id);
      this.loading = true;
      getConfigs(machine_id, site_id).then(
        (res) => {
          if (!res) {
            this.props.history.push('/');
            return;
          }
          this.loading = false;
          this.props.history.push(`/location/${location_id}`);
        }
      );
    }
  }

  selectMachine = (machineId, machineTab = 'overview', location) => {
    let nodeType = 'machine';
    if (machineTab === 'charts') nodeType = 'machine_charts';
    else if (machineTab === 'data-overview') nodeType = 'data_overview';
    this.props.hierarchyActions.selectNode(machineId, nodeType);
    this.props.history.push(location || `/machines/${machineId}/${machineTab}`);
    this.setState({ config: null });
  }

  selectDataOverview = (machineId) => {
    this.props.hierarchyActions.selectNode(machineId, 'data_overview');
    this.props.history.push(`/machines/${machineId}/data-overview`);
  }

  selectSite = (siteId) => {
    this.setState({ config: null });
    this.props.hierarchyActions.selectNode(siteId, 'site');
    this.props.history.push('/');
  }

  switchAccountifRequired = () => {
    const urlParams = new URLSearchParams(this.props.location.search);
    const urlAccountId = urlParams.get('account_id');
    if (!urlAccountId) return;
    const newAccountId = parseInt(urlAccountId, 10);
    if (Number.isNaN(newAccountId)) return;

    const currentAccountId = this.props.user.user.account_id;
    urlParams.delete('account_id');
    const cleanedSearch = urlParams.toString();
    const search = cleanedSearch ? `?${cleanedSearch}` : '';
    const pathname = this.props.history.location.pathname;

    if (newAccountId === currentAccountId) {
      if (this.props.location.search !== search) {
        this.props.history.replace({ pathname, search });
      }
      return;
    }

    this.props.assetDetailsActions.validateAndSwitchAccountWithCallback(
      newAccountId,
      () => {
        this.props.history.replace({ pathname, search });
      }
    );
  }

  isMachineChanged = (nodeId, nodeType) => {
    const currentMachineId = this.props.breadcrumb.machine.id;
    if (nodeType === 'site' || (nodeType === 'machine' && nodeId !== currentMachineId)) return true;
    if (nodeType === 'config' || nodeType === 'location') {
      const path = this.getpath(nodeId, nodeType);
      const params = hierarchyUtils.getHierarchyParams(path, nodeId, nodeType);
      if (params.machine.id && params.machine.id !== currentMachineId) return true;
    }
    return false;
  }

  checkNewSummaryExists = () => {
    if (this.analystSummaryRef.current) {
      const localSummary = this.analystSummaryRef.current.wrappedInstance.getLocalSummary();
      const reduxSummary = this.props.analystSummary;
      return isUnsavedAnalystSummaryExists(localSummary, reduxSummary);
    }
    return false;
  }

  setAnalystSummaryWarning = (value) => {
    this.setState({ analystSummaryWarning: value });
  }

  handleNodeClick = (nodeId, nodeType, machineTab = 'overview', discardAnalystSummary = false, location = null, clearQueryParams = false) => {
    if (this.analystSummaryRef.current) {
      if (!discardAnalystSummary) {
        if (this.checkNewSummaryExists() && this.isMachineChanged(nodeId, nodeType)) {
          this.handleNodeClickPrevParmas = { nodeId, nodeType, machineTab };
          this.setAnalystSummaryWarning(true);
          return;
        }
      }
    }
    let nodePropertyType = null;
    let nodePropertyValue = null;
    if (nodeType === 'config' && !nodeId) {
      const urlParams = new URLSearchParams(this.props.location.search);
      nodePropertyValue = urlParams.get('tag_id') && parseInt(urlParams.get('tag_id'), 10);
      nodePropertyType = 'tag_id';
    }
    switch (nodeType) {
      case 'site':
        this.selectSite(nodeId);
        break;
      case 'machine':
        this.selectMachine(nodeId, machineTab, location);
        break;
      case 'config':
        this.selectConfig(nodeId, nodePropertyType, nodePropertyValue, clearQueryParams);
        break;
      case 'location':
        this.selectLocation(nodeId);
        break;
      case 'machine_charts':
        this.selectMachine(nodeId, 'charts', location);
        break;
      case 'data_overview':
        this.selectMachine(nodeId, 'data-overview');
        break;
      default:
        break;
    }
  };

  getpath = (nodeId, nodeType) => {
    const path = [];
    hierarchyUtils.haspath(this.props.assetHierarchy.hierarchy, nodeId, nodeType, path);
    return path;
  }

  onChevronclick = () => {
    this.setState({ draggingPaneWidth: null });
    const { currentWidth, previousWidth } = this.props.hierarchyViewPane;
    // Match toggleHierarchyView: close always → 0; open restores last width.
    const next = currentWidth
      ? { currentWidth: 0, previousWidth: currentWidth }
      : { currentWidth: previousWidth || 275, previousWidth: 0 };
    localStorage.setItem('hierarchyViewPaneLocal', JSON.stringify(next));

    const { toggleHierarchyView } = this.props.hierarchyActions;
    toggleHierarchyView();
  }

  setEditModeState = (editMode) => {
    this.setState({ editMode });
  }

  handleTabChange = (e, data) => {
    const { activeIndex } = data;
    this.setState({ activeItem: this.tabItems[activeIndex] });
  };

  handleProfileItemClick = (e) => {
    if (e.target.name === 'profile') {
      this.setState({ config: null });
    }
  }

  onCancelWarningWindow = () => {
    this.handleNodeClickPrevParmas = null;
    this.setAnalystSummaryWarning(false);
    this.setDiscardAnalystSummary(false);
  }

  showAnalystWindow = (value) => {
    this.analystSummaryRef.current.wrappedInstance.showAnalystWindow(value);
  }

  onProceedWarningWindow = () => {
    this.setAnalystSummaryWarning(false);
    this.showAnalystWindow(false);
    //  this.handleNodeClickParams is used to identify the call is because of site change or node click
    if (this.handleNodeClickPrevParmas) {
      const { nodeId, nodeType, machineTab } = this.handleNodeClickPrevParmas;
      this.handleNodeClick(nodeId, nodeType, machineTab, true);
      this.handleNodeClickPrevParmas = null;
    } else {
      this.setDiscardAnalystSummary(true);
    }
  }

  setTitleBarHeight = (titleBarHeight) => {
    this.setState({ titleBarHeight });
  }

  setDiscardAnalystSummary = (value) => {
    this.setState({ discardAnalystSummary: value });
  }

  getMenuItems = () => {
    const { activeItem } = this.state;
    const { t } = this.props;

    const tabs = getHomeTabItems(t);

    const items = tabs.map(item => ({
      menuItem: (
        <Menu.Item
          as={Link}
          to={item.path}
          key={item.key}
          active={activeItem === item.key}
        >
          {item.title}
        </Menu.Item>
      ),
      key: item.key,
      path: item.path,
      title: item.title
    }));

    return items;
  };

  onPaneSizeChange = (size) => {
    // Ignore SplitPane clamps while collapsed — controlled size=0 with a
    // non-zero minSize used to re-open the pane via onChange(minSize).
    if (typeof size === 'number' && this.props.hierarchyViewPane.currentWidth) {
      this.setState({ draggingPaneWidth: size });
    }
  }

  onDragFinished = (size) => {
    if (typeof size === 'number') {
      const { updateHierarchyViewPane } = this.props.hierarchyActions;
      // Keep the live width until Redux catches up (cleared in componentDidUpdate)
      // so Pane2 does not briefly snap back to the pre-drag width.
      this.setState({ draggingPaneWidth: size });
      updateHierarchyViewPane(size);

      // Panel is open after a drag — keep previousWidth at 0 so the next
      // collapse toggles to 0 instead of swapping between two open widths.
      localStorage.setItem('hierarchyViewPaneLocal', JSON.stringify({
        currentWidth: size,
        previousWidth: 0
      }));
    }
  }

  getRoutes = () => {
    const { user, currentAccount, t } = this.props;
    const hasOrganisation = !!(
      currentAccount &&
      currentAccount.organisation &&
      currentAccount.organisation.id
    );

    let excludeRouteKeys = ['customer-support-portal'];
    if (!(isPetasenseAdmin(user.user) || isPartnerAdmin(user.user))) {
      excludeRouteKeys = excludeRouteKeys.concat(tabsForPetasenseAndPartnerAdmins);
    }
    if (!(isPetasenseAdmin(user.user) || isPetasenseViewer(user.user))) {
      excludeRouteKeys = excludeRouteKeys.concat(tabsOnlyForPetasenseAdmins);
      if (!isAdmin(user.user) && !isAnalyst(user.user)) {
        excludeRouteKeys = excludeRouteKeys.concat(tabsOnlyForAnalystsAndAdmins);
      }
      if (!isAnalyst(user.user)) {
        excludeRouteKeys = excludeRouteKeys.concat(tabsOnlyForAnalysts);
      }
    }
    if (
      !hasOrganisation
      || !(
        isPetasenseAdmin(user.user)
        || isOrgAdmin(user.user)
        || isPartnerAdmin(user.user)
      )
    ) {
      excludeRouteKeys = excludeRouteKeys.concat(tabsOnlyForOrgAdminsAndPetasenseAdmins);
    }
    // FMEA is an early-access premium feature: keep the FMEA Templates route out
    // of the router (blocks direct navigation) unless the account has it enabled.
    // Petasense staff always pass via isFmeaEnabled.
    if (!isFmeaEnabled(user.user, currentAccount)) {
      excludeRouteKeys = excludeRouteKeys.concat('fmea-templates');
    }

    const tabs = getHomeTabItems(t);
    return tabs.concat(getFlattenedAccountMenuRoutes(t, excludeRouteKeys));
  };

  closeModal = () => (
    this.props.location.state && this.props.location.state.from_app ?
      history.goBack() :
      history.push('/activity')
  );

  render() {
    const {
      user,
      history,
      accountSelectionState,
      breadcrumb,
      hierarchyViewPane: { currentWidth },
      adminDashboard,
      primaryColor,
      partnerName
    } = this.props;

    const { hierarchy } = this.props.assetHierarchy;
    const { config, activeItem, analystSummaryWarning, discardAnalystSummary } = this.state;
    const accountsArray = _.get(user, 'user.accounts');
    const isOrgView = isOrgDashboardRoute(history.location.pathname);
    const showOrgPanel = isOrgView && ORG_SITE_NAV_ENABLED;
    const currentPath = this.props.location.pathname.substring(1);
    // In org view the left panel shows the org->site map; give it a sensible
    // default width when unset. currentWidth === 0 means the user collapsed it
    // (same toggle as the asset hierarchy) and must stay collapsed.
    const ORG_PANEL_DEFAULT_WIDTH = Math.max(viewportWidth() * 0.15, 230);
    let effectiveWidth = pathsWithoutAssetHierarchy.includes(currentPath) ? 0 : currentWidth;
    if (showOrgPanel) {
      effectiveWidth = currentWidth === 0 ? 0 : (currentWidth || ORG_PANEL_DEFAULT_WIDTH);
    } else if (isOrgView) {
      effectiveWidth = 0;
    }
    const { draggingPaneWidth } = this.state;
    const paneWidth = draggingPaneWidth != null ? draggingPaneWidth : effectiveWidth;
    if (accountSelectionState.loadingId) {
      return (
        <div className="full-screen-center">
          <LoadingSvg />
        </div>
      );
    }
    return (
      <HomePageContainer titleBarHeight={this.state.titleBarHeight} className={`home-page${this.props.location.pathname === '/assets' ? ' home-page--assets' : ''}`}>
        {analystSummaryWarning && (
          <AlertPrompt
            message={this.props.t('homePage.confirm.leaveAnalystSummary')}
            secondaryMessage={this.props.t('homePage.confirm.discardAnalystSummaryChanges')}
            onCancel={this.onCancelWarningWindow}
            onProceed={this.onProceedWarningWindow}
          />
        )}
        <Content>
          <SubscriptionBanner />
          <BreadCrumbHeader
            accountsArray={accountsArray}
            location={history.location}
            editMode={this.state.editMode}
            handleLogoClick={this.handleNodeClick}
            checkNewSummaryExists={this.checkNewSummaryExists}
            setAnalystSummaryWarning={this.setAnalystSummaryWarning}
            discardAnalystSummary={discardAnalystSummary}
            analystSummaryWarning={analystSummaryWarning}
            setDiscardAnalystSummary={this.setDiscardAnalystSummary}
            onHeightChange={this.setTitleBarHeight}
            globalActions={(
              <>
                {(isPetasenseAdmin(this.props.user.user) || isPetasenseViewer(this.props.user.user) || isPartnerAdmin(this.props.user.user)) && (
                  !this.props.adminDashboard.adminOpen ? (
                    <StyledLink title={this.props.t('topBar.adminDashboard')} to={Routes.admin}>
                      <User style={{ color: primaryColor }} id="admin" onClick={this.props.adminDashboardActions.toggleAdminDashboard} />
                    </StyledLink>
                  ) : (
                    <StyledLink title={this.props.t('topBar.homePage')} to={Routes.home}>
                      <Home style={{ color: primaryColor }} id="admin" />
                    </StyledLink>
                  )
                )}
                <ReleaseNotesBell />
                {partnerName === 'Petasense' && (
                  <a
                    title={this.props.t('topBar.customerSupportPortal')}
                    href="https://petasense.freshdesk.com/support/home"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <QuestionCircle style={{ color: primaryColor, fontSize: '16px' }} id="help" />
                  </a>
                )}
                <ProfileDropdown onClick={this.handleProfileItemClick} fixedPosition={false} />
              </>
            )}
            machineStatus={breadcrumb.machine.id && !adminDashboard.adminOpen ? (
              <AnalystSummaryView
                ref={this.analystSummaryRef}
                machine_id={breadcrumb.machine.id}
                location={history.location}
              />
            ) : null}
          />
          <HierarchyContainer direction="row">
            <CustomSplitPane
              split="vertical"
              onChange={this.onPaneSizeChange}
              onDragFinished={this.onDragFinished}
              size={paneWidth}
              defaultSize={effectiveWidth}
              // visualViewportWidth : changes the width based on what the user sees instead of browser.
              // minSize must be 0 when collapsed, otherwise controlled size={0} is clamped
              // back up and the close-chevron appears to do nothing.
              minSize={!effectiveWidth || (isOrgView && !showOrgPanel) ? 0 : Math.max(viewportWidth() * 0.15, 230)}
              maxSize={!effectiveWidth || (isOrgView && !showOrgPanel) ? 0 : Math.min(viewportWidth() * 0.35, viewportWidth() - 100)}
              width={paneWidth}
              disableDrag={!effectiveWidth}
            >
              <Pane>
                {showOrgPanel && (
                  <MainContainer id="org-site-hierarchy">
                    <OrgSiteHierarchyPanel activeOrgId={getOrgIdFromPath(history.location.pathname)} />
                  </MainContainer>
                )}
                {!isOrgView && this.props.loadingReducer.GET_ASSET_HIERARCHY && <LoadingSvg />}
                {!isOrgView && hierarchy.length > 0 && (
                  <MainContainer id="hierarchy-page">
                    <HierarchyView
                      handleNodeClick={this.handleNodeClick}
                      config_id={this.state.config ? this.state.config.id || this.state.config.config_id : -1}
                      onChevronclick={this.onChevronclick}
                      siteName={!_.isEmpty(hierarchy) ? hierarchy[0].node_name : ''}
                      editMode={this.state.editMode}
                      setEditModeState={this.setEditModeState}
                      location={history.location}
                      orgBreadcrumb={() => (
                        <OrgSiteBreadcrumb
                          checkNewSummaryExists={this.checkNewSummaryExists}
                          setAnalystSummaryWarning={this.setAnalystSummaryWarning}
                          discardAnalystSummary={discardAnalystSummary}
                          analystSummaryWarning={analystSummaryWarning}
                          setDiscardAnalystSummary={this.setDiscardAnalystSummary}
                        />
                      )}
                    />
                  </MainContainer>
                )}
                {!isOrgView && this.state.editMode && (
                  <Overlay
                    left={`${this.props.hierarchyViewPane.currentWidth}px`}
                    width={`calc(100vw - ${this.props.hierarchyViewPane.currentWidth}px)`}
                    onClick={() => this.setEditModeState(false)}
                  />
                )}
              </Pane>
              <Pane hierarchyViewPane={this.props.hierarchyViewPane}>
                {!adminDashboard.adminOpen && (showOrgPanel || !isOrgView) && (
                  <>
                    {!effectiveWidth && !this.state.editMode
                      && (showOrgPanel || !pathsWithoutAssetHierarchy.includes(currentPath)) && (
                      <ChevronContainer id="hierarchy-page-close" left onClick={() => this.onChevronclick()}>
                        <FlexContainer height="inherit" justifyContent="center" alignItems="center">
                          <ChevronRight size="sm" />
                        </FlexContainer>
                      </ChevronContainer>
                    )}
                    {!!effectiveWidth && !this.state.editMode && (
                      <ChevronContainer id="hierarchy-page-open" right onClick={() => this.onChevronclick()}>
                        <FlexContainer height="inherit" justifyContent="center" alignItems="center">
                          <ChevronLeft size="sm" />
                        </FlexContainer>
                      </ChevronContainer>
                    )}
                  </>
                )}
                {!accountSelectionState.loadingId && (
                  <SwitchContainer
                    id="home-page-switch-container"
                    open={!isOrgView && !!effectiveWidth && hierarchy.length > 0}
                  >
                    {this.tabItems.indexOf(activeItem) !== -1 && (
                      <DynamicTabs
                        menu={{ secondary: true }}
                        panes={this.getMenuItems()}
                        activeIndex={this.tabItems.indexOf(activeItem)}
                        onTabChange={this.handleTabChange}
                        bufferSpace={45}
                      />
                    )}
                    <Suspense fallback={(
                        <LoadingSvg height="100%" />
                      )}
                    >
                      <ErrorBoundary name="route-home-content" variant="page" resetKeys={[this.props.location.pathname]}>
                      <Switch>
                        <Route
                          exact
                          path="/machines/build"
                          render={() => <MachineBuilderModal close={() => this.props.history.goBack()} />}
                        />
                        <Route
                          exact
                          path="/templates/build"
                          render={() => <MachineBuilderModal buildType="template" close={() => this.props.history.goBack()} />}
                        />
                        <Route
                          path="/machines/new"
                          render={() => <MachineAdd close={() => this.props.history.goBack()} />}
                        />
                        <Route
                          path="/templates/new"
                          render={() => <MachineAdd close={() => this.props.history.goBack()} addType="template" />}
                        />
                        <Route
                          path="/devices/new"
                          component={SensorAdd}
                        />
                        <Route path="/templates/:template_id" component={TemplateDetails} />
                        <Route
                          path="/machines/:machine_id/data-overview"
                          render={() => <DataOverview />}
                        />
                        <Route path="/machines/:machine_id" component={MachineDetails} />
                        <Route exact path="/profile" component={ProfileSettings} />
                        <Route exact path="/organisations/:orgId/dashboard" component={OrgViewComponent} />
                        { this.getRoutes().map(item => <Route key={item.key} exact path={item.path} component={item.component} />) }
                        <Route
                          path="/configs/:config_id"
                          render={() => {
                            if (!config || (config && config.default)) return <ChartsView updateConfig={this.updateConfig} />;
                            return <MultilineChartView />;
                          }}
                        />
                        <Route
                          path="/configs"
                          render={() => <LoadingSvg height="100%" />}
                        />
                        <Route
                          path="/location/:location_id"
                          render={() => <LocationChartView />}
                        />
                        { (isPetasenseAdmin(this.props.user.user) || isPetasenseViewer(this.props.user.user) || isPartnerAdmin(this.props.user.user)) && (
                        <Route
                          path="/admin-dashboard"
                          component={AdminDashboard}
                        />
                        )}
                        <Route
                          path="/tasks/:task_id"
                          exact
                          render={props => (
                            <TaskDetailsModal
                              isEditMode
                              closeModal={() => {
                                this.closeModal();
                                this.props.taskActions.closeTaskModal();
                              }}
                              {...props}
                            />
                          )}
                        />
                        <Route
                          path="/repairs/:repair_id"
                          exact
                          render={props => (
                            <RepairDetailsModal
                              isEditMode
                              closeModal={() => {
                                this.closeModal();
                                this.props.repairActions.closeRepairModal();
                              }}
                              {...props}
                            />
                          )}
                        />
                        <Route exact path="*" component={PageNotFound} />
                      </Switch>
                      </ErrorBoundary>
                    </Suspense>
                  </SwitchContainer>
                )}
              </Pane>
            </CustomSplitPane>
          </HierarchyContainer>
        </Content>
        <HomePageModals />
      </HomePageContainer>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    assetHierarchyReducer,
    accountSelectionState,
    breadcrumb,
    machineDetails,
    user,
    currentAccount,
    hierarchyViewPane,
    loadingReducer,
    adminDashboard,
    companyReducer
  } = state;
  return {
    user,
    currentAccount,
    accountSelectionState,
    assetHierarchy: assetHierarchyReducer.assetInfo,
    breadcrumb,
    analystSummary: machineDetails.AS.summary,
    hierarchyViewPane,
    loadingReducer,
    adminDashboard,
    primaryColor: companyReducer.partner.theme.primaryColor,
    partnerName: companyReducer.partner.name
  };
};

const mapDispatchToProps = dispatch => ({
  assetDetailsActions: bindActionCreators(assetDetailsActions, dispatch),
  hierarchyActions: bindActionCreators(hierarchyActions, dispatch),
  selectSite: bindActionCreators(selectAccount, dispatch),
  breadcrumbActions: bindActionCreators(breadcrumbActions, dispatch),
  adminDashboardActions: bindActionCreators(adminDashboardActions, dispatch),
  taskActions: bindActionCreators(taskActions, dispatch),
  repairActions: bindActionCreators(repairActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(HomePage));
